import React from 'react';

import { StyledImage } from './index.styled';
import { capitalizeFirstLetter } from 'src/viewLayer/helpers/textHelpers';

// Phosphor icons
import {
  BookBookmark,
  Popcorn,
  SuitcaseRolling,
  UsersThree,
  Briefcase,
  Shapes,
  DotsThreeOutline,
  Devices,
  Shuffle
} from '@phosphor-icons/react';
// question Language Level node answers
import { ReactComponent as QuestionLanguageLevelNotMuch } from './icons/questionAnswers/languageLevelNotMuch.svg';
import { ReactComponent as QuestionLanguageLevelSome } from './icons/questionAnswers/languageLevelSome.svg';
import { ReactComponent as QuestionLanguageLevelALot } from './icons/questionAnswers/languageLevelALot.svg';
// question Time node answers
import { ReactComponent as QuestionTimeThirtyMins } from './icons/questionAnswers/timeThirtyMins.svg';
import { ReactComponent as QuestionTimeTwoHours } from './icons/questionAnswers/timeTwoHours.svg';
import { ReactComponent as QuestionTimeUpToFourHours } from './icons/questionAnswers/timeUpToFourHours.svg';
import { ReactComponent as QuestionTimeMoreThanFourHours } from './icons/questionAnswers/timeMoreThanFourHours.svg';
// motivation infopages
import BulbImage from './icons/infoPage/motivation/bulb.png';
import CoffeeCupImage from './icons/infoPage/motivation/coffee-cup.png';
import GlobeColouredImage from './icons/infoPage/motivation/globe-coloured.png';
import LuggageImage from './icons/infoPage/motivation/luggage.png';
import NotepadImage from './icons/infoPage/motivation/notepad.png';
// time goal infopage
import WomanWithPhoneImage from './icons/infoPage/timeGoal/woman-with-phone.png';
// age infopage
import { ReactComponent as Calendar } from './icons/infoPage/age/calendar.svg';
// profile creation infopage
import { ReactComponent as Puzzle } from './icons/infoPage/profileCreation/puzzle.svg';
// timeGoal infopage
import { ReactComponent as Globe } from './icons/infoPage/languageLevel/globe.svg';
import { ReactComponent as Headphones } from './icons/infoPage/languageLevel/headphones.svg';
import TeacupImage from './icons/infoPage/languageLevel/teacup.png';
import ButterflyImage from './icons/infoPage/languageLevel/butterfly.png';
import SaladImage from './icons/infoPage/languageLevel/salad.png';
// verification page
import { ReactComponent as Envelope } from './icons/infoPage/verification/envelope.svg';

const WomanWithPhone = (): JSX.Element => <img src={WomanWithPhoneImage} alt="A smiling woman with a phone" />;
const Butterfly = (): JSX.Element => <img src={ButterflyImage} alt="A butterfly" />;
const Salad = (): JSX.Element => <img src={SaladImage} alt="A salad" />;
const Teacup = (): JSX.Element => <img src={TeacupImage} alt="A teacup" />;
const Bulb = (): JSX.Element => <StyledImage src={BulbImage} alt="A bulb" />;
const CoffeeCup = (): JSX.Element => <StyledImage src={CoffeeCupImage} alt="A coffee cup" />;
const GlobeColoured = (): JSX.Element => <StyledImage src={GlobeColouredImage} alt="A globe" />;
const Luggage = (): JSX.Element => <StyledImage src={LuggageImage} alt="A suitcase" />;
const Notepad = (): JSX.Element => <StyledImage src={NotepadImage} alt="A notepad" />;

type SVG =  React.FC<React.SVGProps<SVGSVGElement>>;

const images: Record<string, SVG | React.FC<{ size: number }>> = {
  BookBookmark: ({ ...props }) => <BookBookmark weight="duotone" {...props} />,
  Popcorn: ({ ...props }) => <Popcorn weight="duotone" {...props} />,
  SuitcaseRolling: ({ ...props }) => <SuitcaseRolling weight="duotone" {...props} />,
  UsersThree: ({ ...props }) => <UsersThree weight="duotone" {...props} />,
  Briefcase: ({ ...props }) => <Briefcase weight="duotone" {...props} />,
  Shapes: ({ ...props }) => <Shapes weight="duotone" {...props} />,
  DotsThreeOutline: ({ ...props }) => <DotsThreeOutline weight="duotone" {...props} />,
  Devices: ({ ...props }) => <Devices weight="duotone" {...props} />,
  QuestionLanguageLevelNotMuch,
  QuestionLanguageLevelSome,
  QuestionLanguageLevelALot,
  QuestionTimeThirtyMins,
  QuestionTimeTwoHours,
  QuestionTimeUpToFourHours,
  QuestionTimeMoreThanFourHours,
  QuestionTimeAskLater: ({ ...props }) => <DotsThreeOutline weight="duotone" {...props} />,
  Shuffle: ({ ...props }) => <Shuffle {...props} />,
  Bulb,
  CoffeeCup,
  GlobeColoured,
  Luggage,
  Notepad,
  WomanWithPhone,
  Calendar,
  Puzzle,
  Globe,
  Headphones,
  Teacup,
  Butterfly,
  Salad,
  Envelope
};

export const getImageComponent = (filename: string, customSize?: number, customColor?: string): JSX.Element => {
  const componentName = filename.split(/[_|-]/).map((word) => capitalizeFirstLetter(word)).join('');
  const Component = images[componentName];
  return <Component size={customSize} color={customColor} />;
};
